// src/pages/ScanMultiplePage.js
// Scan Multiple (David, 10/29): choose where the tools are going once, then
// scan them one after another.
//   👤 To a person + job — each tool becomes a pending transfer to that
//      person (and job); they get ONE text and accept on the Incoming screen.
//   🚛 Load a truck + job — loads the tools onto the truck for that job
//      (this replaces the old Load Truck screen; /load-truck opens it).
// After each scan the item pops up with ✓ Correct / ↻ Re-scan; Correct goes
// straight to the next scan, and Finish is always on screen.
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { canTransferItems } from '../permissions';
import {
  getLocations, getWorkers, getItemByQR, getItemById, getItems,
  bulkLoadTruck, initiateBatchTransfer,
} from '../services/api';
import SearchPicker from '../components/SearchPicker';
import JobPicker from '../components/JobPicker';
import MultiScanner, { qrIdFrom } from '../components/MultiScanner';

// Same roles as "Load to Truck" on a tool's page.
const canLoadTruck = (role) => ['power_user', 'general_superintendent', 'site_supervisor', 'foreman', 'driver'].includes(role);
const roleLabel = (r) => (r || '').replace(/_/g, ' ');

export default function ScanMultiplePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { worker } = useContext(AuthContext);
  const role = worker?.role;

  const startMode = params.get('mode') === 'truck' ? 'truck' : params.get('mode') === 'person' ? 'person' : null;
  const [mode, setMode] = useState(startMode);
  const [step, setStep] = useState(startMode === 'truck' ? 'truck' : startMode === 'person' ? 'person' : 'mode');

  const [workers, setWorkers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [places, setPlaces] = useState([]);      // jobsites + warehouse etc.
  const [loading, setLoading] = useState(true);

  const [person, setPerson] = useState(null);
  const [truck, setTruck] = useState(null);
  const [job, setJob] = useState(null);           // location, or null = no job
  const [jobSource, setJobSource] = useState(null);

  const [items, setItems] = useState([]);         // confirmed scans, in order
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([getWorkers({ active: true }), getLocations({ type: 'truck' }), getLocations()])
      .then(([w, t, l]) => {
        setWorkers((w.data.workers || []).filter((x) => x.worker_id !== worker?.worker_id));
        setTrucks(t.data.locations || []);
        setPlaces((l.data.locations || []).filter((x) => ['jobsite', 'warehouse', 'external'].includes(x.location_type)));
      })
      .catch(() => setError('Could not load people and jobs. Check your connection.'))
      .finally(() => setLoading(false));
  }, [worker]);

  if (!canTransferItems(role)) {
    return (
      <div className="screen"><div className="screen-content">
        <div className="empty-state"><p>Your login doesn't include transfers.</p></div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Back to Home</button>
      </div></div>
    );
  }

  // ── Scanning rules ──────────────────────────────────────
  const lookup = async (text) => {
    const { qr, itemId } = qrIdFrom(text);
    const res = itemId ? await getItemById(itemId) : await getItemByQR(qr);
    return res.data.item;
  };
  const search = async (text) => {
    const res = await getItems({ search: text, limit: 5 });
    return (res.data.items || [])[0] || null;
  };
  const check = (it) => {
    if (items.some((x) => x.item_id === it.item_id)) return { block: 'Already in this list.' };
    if (it.current_status === 'pending_transfer' || it.has_pending_transfer) return { block: 'This tool already has a transfer waiting to be accepted.' };
    if (mode === 'person') {
      if (it.item_type === 'bulk') return { block: 'Bulk materials can\'t be transferred to a person here.' };
      if (it.current_worker_id && it.current_worker_id === person?.worker_id) return { block: `${person.display_name} already has this tool.` };
      if (['retired', 'lost'].includes(it.current_status)) return { block: `This tool is marked ${it.current_status}.` };
      if (it.current_status === 'in_repair') return { warn: 'This tool is marked in repair.' };
    } else if (it.current_status !== 'available') {
      return { warn: `Currently ${(it.current_status || '').replace(/_/g, ' ')}. It will still be loaded.` };
    }
    return {};
  };
  const onCorrect = (it) => setItems((prev) => (prev.some((x) => x.item_id === it.item_id) ? prev : [...prev, it]));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'person') {
        const res = await initiateBatchTransfer({
          item_ids: items.map((i) => i.item_id), to_worker_id: person.worker_id, to_location_id: job?.location_id || null,
        });
        setResult({ ok: res.data.created || [], failed: res.data.failed || [], sms: res.data.sms_sent });
      } else {
        const res = await bulkLoadTruck({
          items: items.map((i) => ({ item_id: i.item_id, quantity: 1 })),
          truck_location_id: truck.location_id,
          performed_by: worker.worker_id,
          procore_project_id: job?.procore_project_id || null,
          procore_project_name: job?.procore_project_name || job?.name || null,
          job_pick_source: jobSource,
          notes: `Loaded for ${job?.name || 'unspecified jobsite'} by ${worker.display_name}`,
        });
        setResult({ ok: res.data.loaded || [], failed: res.data.failed || [] });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Nothing was lost: try again.');
      const failed = err.response?.data?.failed;
      if (failed) setResult({ ok: [], failed });
    } finally {
      setSubmitting(false);
    }
  };

  const nameOf = (id) => items.find((i) => i.item_id === id)?.name || 'Tool';
  const dest = mode === 'person'
    ? `${person?.display_name || ''}${job ? ` · ${job.name}` : ''}`
    : `${truck?.name || ''}${job ? ` → ${job.name}` : ''}`;

  const back = () => {
    setError('');
    if (step === 'review') return setStep('scan');
    if (step === 'scan') {
      if (items.length && !window.confirm(`Leave scanning? The ${items.length} scanned tool${items.length === 1 ? '' : 's'} will be cleared.`)) return;
      setItems([]);
      return setStep('job');
    }
    if (step === 'job') return setStep(mode === 'person' ? 'person' : 'truck');
    if ((step === 'person' || step === 'truck') && !startMode) { setMode(null); return setStep('mode'); }
    navigate(-1);
  };

  const header = (title, sub) => (
    <div className="screen-header">
      <button onClick={back} aria-label="Back" style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <div>
        <h2>{title}</h2>
        {sub && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{sub}</p>}
      </div>
    </div>
  );

  if (loading) return <div className="screen"><div className="loading"><div className="spinner" /><span>Loading...</span></div></div>;

  // ── Result ──────────────────────────────────────────────
  if (result) {
    const isPerson = mode === 'person';
    return (
      <div className="screen">
        {header(isPerson ? 'Transfers Sent' : 'Truck Loaded', dest)}
        <div className="screen-content">
          {result.ok.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8, color: 'var(--status-available, #22c55e)', fontSize: '0.95rem' }}>
                {isPerson ? `⏳ ${result.ok.length} waiting for ${person.display_name} to accept` : `✅ Loaded (${result.ok.length})`}
              </h3>
              {result.ok.map((i) => (
                <p key={i.item_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem' }}>
                  {i.name || nameOf(i.item_id)}{i.asset_tag ? ` · ${i.asset_tag}` : ''}
                </p>
              ))}
              {isPerson && (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>
                  {result.sms ? 'One text was sent for all of them.' : "The text couldn't be sent; they can accept from Incoming in the app."}
                </p>
              )}
            </div>
          )}
          {result.failed.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8, color: 'var(--status-lost)', fontSize: '0.95rem' }}>⚠️ Not done ({result.failed.length})</h3>
              {result.failed.map((f, i) => (
                <p key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem', color: 'var(--status-lost)' }}>
                  {nameOf(f.item_id)}: {f.error}
                </p>
              ))}
            </div>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/')}>Back to Home</button>
          <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => {
            setItems([]); setResult(null); setJob(null); setPerson(null); setTruck(null);
            setStep(startMode ? (startMode === 'truck' ? 'truck' : 'person') : 'mode');
            if (!startMode) setMode(null);
          }}>Scan Another Batch</button>
        </div>
      </div>
    );
  }

  // ── Step: what are we doing ─────────────────────────────
  if (step === 'mode') return (
    <div className="screen">
      {header('Scan Multiple', 'Choose where the tools are going, then scan them')}
      <div className="screen-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <button className="btn home-tile-btn" style={{ minHeight: 96, fontSize: '1.15rem', borderRadius: 18, fontWeight: 700, flexDirection: 'column', gap: 4 }}
          onClick={() => { setMode('person'); setStep('person'); }}>
          <span style={{ fontSize: '1.8rem' }}>👤</span>
          To a person and job
        </button>
        {canLoadTruck(role) && (
          <button className="btn home-tile-btn" style={{ minHeight: 96, fontSize: '1.15rem', borderRadius: 18, fontWeight: 700, flexDirection: 'column', gap: 4 }}
            onClick={() => { setMode('truck'); setStep('truck'); }}>
            <span style={{ fontSize: '1.8rem' }}>🚛</span>
            Load a truck
          </button>
        )}
      </div>
    </div>
  );

  // ── Step: person ────────────────────────────────────────
  if (step === 'person') return (
    <div className="screen">
      {header('Transfer To', 'Who is getting these tools?')}
      <div className="screen-content">
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
        <SearchPicker
          items={workers}
          getKey={(w) => w.worker_id}
          getLabel={(w) => w.display_name}
          getSub={(w) => roleLabel(w.role || w.at_role)}
          icon="👤"
          onPick={(w) => { setPerson(w); setJob(null); setStep('job'); }}
          placeholder="Type a name…"
          allTitle="Everyone"
          emptyText="No one found"
        />
      </div>
    </div>
  );

  // ── Step: truck ─────────────────────────────────────────
  if (step === 'truck') return (
    <div className="screen">
      {header('Select Truck')}
      <div className="screen-content">
        {trucks.length === 0
          ? <div className="empty-state"><div style={{ fontSize: '2rem' }}>🚛</div><p>No trucks set up.</p></div>
          : <SearchPicker
              items={trucks}
              getKey={(t) => t.location_id}
              getLabel={(t) => t.name}
              getSub={(t) => `${t.item_count || 0} items on board`}
              icon="🚛"
              onPick={(t) => { setTruck(t); setJob(null); setStep('job'); }}
              placeholder="Type a truck name…"
              allTitle="Trucks"
              emptyText="No trucks found"
            />}
      </div>
    </div>
  );

  // ── Step: job ───────────────────────────────────────────
  if (step === 'job') return (
    <div className="screen">
      {header(mode === 'person' ? 'Which Job?' : 'Delivering To', mode === 'person' ? `For ${person.display_name}` : truck.name)}
      <div className="screen-content">
        <JobPicker
          locations={mode === 'person' ? places : places.filter((p) => p.location_type === 'jobsite')}
          forWorker={mode === 'person' ? person : worker}
          onPick={(l, source) => { setJob(l); setJobSource(source); setStep('scan'); }}
        />
        {mode === 'person' && (
          <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => { setJob(null); setJobSource(null); setStep('scan'); }}>
            Skip: no job (only the person changes)
          </button>
        )}
      </div>
    </div>
  );

  // ── Step: scan ──────────────────────────────────────────
  if (step === 'scan') return (
    <div className="screen">
      {header(mode === 'person' ? 'Scan Tools' : 'Scan Items', dest)}
      <div className="screen-content">
        <MultiScanner
          lookup={lookup}
          search={search}
          check={check}
          onCorrect={onCorrect}
          count={items.length}
          finishDisabled={items.length === 0}
          finishLabel={items.length === 0 ? 'Scan a tool to begin' : `Finish: ${items.length} tool${items.length === 1 ? '' : 's'}`}
          onFinish={() => setStep('review')}
        />
        {items.length > 0 && (
          <div className="card" style={{ marginTop: 4 }}>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Scanned so far</p>
            {items.slice().reverse().map((i) => (
              <p key={i.item_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem' }}>
                ✓ {i.name}{i.asset_tag ? <span style={{ color: 'var(--text-muted)' }}> · {i.asset_tag}</span> : null}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── Step: review ────────────────────────────────────────
  return (
    <div className="screen">
      {header('Review', dest)}
      <div className="screen-content">
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{mode === 'person' ? 'To' : 'Truck'}</p>
            <p style={{ fontWeight: 700 }}>{mode === 'person' ? `👤 ${person.display_name}` : `🚛 ${truck.name}`}</p>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>📍 {job ? job.name : 'No job'}</p>
          </div>
          <div className="divider" />
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '8px 0' }}>{items.length} tool{items.length === 1 ? '' : 's'}</p>
          {items.map((i) => (
            <div key={i.item_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem' }}>{i.name}</p>
                {i.asset_tag && <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{i.asset_tag}</p>}
              </div>
              <button aria-label={`Remove ${i.name}`} onClick={() => setItems((prev) => prev.filter((x) => x.item_id !== i.item_id))}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem', padding: '4px 8px' }}>×</button>
            </div>
          ))}
          {mode === 'person' && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 10 }}>
              {person.display_name} gets one text and accepts them in the app (24 hours).
            </p>
          )}
        </div>
        <button className="btn btn-primary" onClick={submit} disabled={submitting || items.length === 0}>
          {submitting ? 'Sending…' : mode === 'person'
            ? `📱 Send ${items.length} to ${person.display_name.split(' ')[0]}`
            : `🚛 Load ${items.length} onto ${truck.name}`}
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => setStep('scan')} disabled={submitting}>
          ← Scan More
        </button>
      </div>
    </div>
  );
}
